import React, { useEffect, useMemo, useState } from 'react';
import css from './styles.module.scss';
import { SongCard } from './components/song-card';
import { MusicListItemDto } from '~/models/music/music.dto';
import {Box, ButtonBase, Divider, Drawer, IconButton, TextField, Typography} from '@material-ui/core';
import { ChipsFilter } from '~/common/chips-filter';
import { DateRange, DateRangeDelimiter, DateRangePicker, LocalizationProvider } from '@material-ui/pickers-next';
import MomentUtils from '@material-ui/pickers-next/adapter/moment';
import { Search } from '~/common/search';
import moment from 'moment';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { getSearchSongFilterActions } from '../../store/actions';
import { searchSongFilter } from '../../store/selectors';
import { useSelector } from 'react-redux';
import { Pagination } from '@material-ui/lab';
import { orderBy } from 'lodash';
import { useDateFormat } from '~/common/date-format';
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FilterListIcon from "@material-ui/icons/FilterList";

interface SongsGridProps {
    songs: MusicListItemDto[];
    setFilters(filters: { search?: string; startDate?: string; endDate?: string }): void;
}

enum DRAWER_TYPES {
    FILTER = 'FILTER',
}

export const SongsGrid: React.FC<SongsGridProps> = ({ songs, setFilters }) => {
    const { selectedDateFormat } = useDateFormat();
    const [filteredSongs, setFilteredSongs] = useState<MusicListItemDto[]>([]);
    const [songStatusFilter, setSongStatusFilter] = useState<string[]>([]);
    const [songDealStatusFilter, setSongDealStatusFilter] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [dateFilter, setDateFilter] = React.useState<DateRange<Date>>([null, null]);
    const dispatch = useThunkDispatch();

    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 9;
    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfItemDeals = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = filteredSongs.slice(indexOfItemDeals, indexOfLastItem);
    const pagesCount = Math.ceil(filteredSongs.length / ITEMS_PER_PAGE);

    const statuses = useMemo(() => {
        const songStatuses: string[] = [];
        for (const song of songs) {
            if (song.showSongStatus.trim() && !songStatuses.includes(song.showSongStatus)) {
                songStatuses.push(song.showSongStatus);
            }
        }
        return songStatuses;
    }, [songs]);

    const dealStatuses = useMemo(() => {
        const songDealStatuses: string[] = [];
        for (const song of songs) {
            if (song.showSongDealStatus.trim() && !songDealStatuses.includes(song.showSongDealStatus)) {
                songDealStatuses.push(song.showSongDealStatus);
            }
        }
        return songDealStatuses;
    }, [songs]);

    useEffect(() => {
        const typeFilteredSongs = songs.filter(song => (songStatusFilter.length ? songStatusFilter.includes(song.showSongStatus) : true));
        const statusFilteredDeals = typeFilteredSongs.filter(song =>
            songDealStatusFilter.length ? songDealStatusFilter.includes(song.showSongDealStatus) : true
        );
        const searchFilteredSongs = statusFilteredDeals.filter(
            song =>
                (song.songName && song.songName.toLowerCase().includes(searchValue.toLowerCase())) ||
                (song.assetCode && song.assetCode.toLowerCase().includes(searchValue.toLowerCase()))
        );
        const [minDate, maxDate] = dateFilter;
        const dateFilteredSongs = searchFilteredSongs.filter(song => {
            const compareDate = moment(song.releaseDate);
            return !(compareDate.isBefore(minDate) || compareDate.isAfter(maxDate));
        });
        const data = orderBy(dateFilteredSongs || [], ['songName'], ['asc']);
        setFilteredSongs(data);
        setFilters({
            search: searchValue.toLowerCase(),
            startDate: minDate ? moment(minDate).format('YYYY-MM-DD') : undefined,
            endDate: maxDate ? moment(maxDate).format('YYYY-MM-DD') : undefined,
        });
    }, [searchValue, songs, songDealStatusFilter, dateFilter, songStatusFilter, setFilters]);

    const onChangeSearchFilter = (value: any) => {
        dispatch(getSearchSongFilterActions.success({ searchSongFilter: value }));
    };

    const getSearchDealValue = useSelector(searchSongFilter);

    useEffect(() => {
        if (getSearchDealValue !== undefined) setSearchValue(getSearchDealValue);
    }, [getSearchDealValue]);

    const [drawerData, setDrawerData] = useState<{ isOpen: boolean; type?: DRAWER_TYPES; meta?: any }>({ isOpen: false });
    const closeDrawer = () => {
        setDrawerData({ isOpen: false, type: undefined, meta: undefined });
    };
    const openDrawer = (type: DRAWER_TYPES, meta?: any) => {
        setDrawerData({ isOpen: true, type, meta });
    };

    const openFilterDrawer = () => {
        openDrawer(DRAWER_TYPES.FILTER);
    };

    return (
        <div className={css.module}>
            <div className={css.filtersContainer}>
                <ButtonBase>
                    <FilterListIcon className={css.filtersIcon} onClick={openFilterDrawer} />
                </ButtonBase>
                {songs.length > ITEMS_PER_PAGE && (filteredSongs.length > ITEMS_PER_PAGE || searchValue || dateFilter[0] || dateFilter[1]) && (
                    <div className={css.dateSearchContainer}>
                        <Search search={getSearchDealValue} onSearchChange={onChangeSearchFilter} className={css.search} />
                    </div>
                )}
            </div>
            {currentItems.map((song: any, index: any) => (
                <SongCard key={index} data={song} />
            ))}
            {filteredSongs.length > ITEMS_PER_PAGE && (
                <Box className={css.paginationWrapper}>
                    <Typography className={css.total}>Total: {filteredSongs.length}</Typography>
                    <Pagination
                        className={css.pagination}
                        count={pagesCount}
                        page={currentPage}
                        onChange={(event: any, value: any) => {
                            setCurrentPage(value);
                        }}
                        variant="outlined"
                        shape="rounded"
                        hideNextButton
                        hidePrevButton
                    />
                </Box>
            )}
            <Box className={css.DrawerContainer}>
                <Drawer variant="persistent" anchor="right" open={drawerData.isOpen} id="deal-filter-drawer" className={css.rightDrawer}>
                    <Box className={css.contentWrapper}>
                        <Box className={css.closeDrawer}>
                            <IconButton onClick={closeDrawer}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography style={{ fontSize: '1rem' }}>Close</Typography>
                        </Box>
                        <Divider />
                        {drawerData.type === DRAWER_TYPES.FILTER && (
                            <div className={css.chipFilterContainer}>
                                <LocalizationProvider dateAdapter={MomentUtils}>
                                    <DateRangePicker
                                        startText="Release Date From"
                                        endText="Release Date To"
                                        inputFormat={selectedDateFormat}
                                        value={dateFilter}
                                        onChange={newValue => setDateFilter(newValue)}
                                        renderInput={(startProps, endProps) => (
                                            <React.Fragment>
                                                <TextField {...startProps} className={css.datePickerInput} variant="standard" />
                                                <DateRangeDelimiter> to </DateRangeDelimiter>
                                                <TextField {...endProps} className={css.datePickerInput} variant="standard" />
                                            </React.Fragment>
                                        )}
                                    />
                                </LocalizationProvider>
                                {statuses && (
                                    <div className={css.chipFilterWrapper}>
                                        <Box pb={1}>
                                            <Typography className={css.statustitle}>Song status filters:</Typography>
                                        </Box>
                                        <ChipsFilter chips={statuses} filters={songStatusFilter} onFilterChange={setSongStatusFilter} className={css.chipFilter} />
                                    </div>
                                )}
                                {dealStatuses && (
                                    <div className={css.chipFilterWrapper}>
                                        <Box pb={1}>
                                            <Typography className={css.statustitle}>Song deal status filters:</Typography>
                                        </Box>
                                        <ChipsFilter chips={dealStatuses} filters={songDealStatusFilter} onFilterChange={setSongDealStatusFilter} className={css.chipFilter} />
                                    </div>
                                )}
                            </div>
                        )}
                    </Box>
                </Drawer>
            </Box>
        </div>
    );
};
